import { m, AnimatePresence } from 'framer-motion';
import { Clock, Power, Lock, Unlock, Menu, LogOut, X } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

import TerminalStepper from './TerminalStepper';

import { appConfig } from '@core/config/appConfig';
import { useAuth } from '@core/context/AuthContext';
import { useUI } from '@core/context/UIContext';
import { Drawer } from '@core/ui';

/**
 * TerminalHeader - Persistent top navigation for the Withdrawal Terminal.
 * - NEW: Mobile Title and Hamburger Menu for high-density navigation.
 */
const TerminalHeader = ({
    onExit,
    onActivateKiosk,
    phaseTitle,
    phaseSubtitle,
    step,
    terminalMode
}) => {
    const { user, isTerminalMode, logout } = useAuth();
    const { currentTime, isScrolled, pageTitle } = useUI();
    const navigate = useNavigate();
    const { brand: brandConfig, navigation: allNavItems } = appConfig;
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // UNIFIED NAVIGATION LOGIC: preserving order and applying Lockdown filter
    const activeNavItems = allNavItems.filter(item => {
        // 1. Role Filter (Case-insensitive comparison for robustness)
        const userRole = user?.role?.toUpperCase();
        const requiredRole = item.requiredRole?.toUpperCase();
        const hasRole = !requiredRole || requiredRole === userRole;
        if (!hasRole) return false;

        // 2. Lockdown Filter: Only Terminal and Audit (History) allowed
        if (isTerminalMode) {
            const allowedKioskPaths = ['/terminal', '/audit'];
            return allowedKioskPaths.includes(item.path);
        }

        return true;
    });

    const name = user?.displayName || user?.email || 'User';
    const initials = user?.initials || name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

    const navItemBaseClass = "flex items-center gap-2.5 px-4 h-10 rounded-xl transition-all duration-200 text-[10px] font-black uppercase tracking-[0.15em]";
    const isReplenishing = terminalMode === 'REPLENISHMENT';
    const activeClass = `bg-white/10 ${isReplenishing ? 'text-emerald-400' : 'text-accent'} border border-white/10 shadow-inner backdrop-blur-xl`;
    const inactiveClass = "text-white/40 hover:text-white/80 hover:bg-white/5";

    const handleMobileNav = (path) => {
        navigate(path);
        setIsMobileMenuOpen(false);
    };

    const handleLogout = async () => {
        if (isTerminalMode) {
            // In Kiosk Mode, "LogOut" acts as a request to release the terminal first
            onExit();
        } else {
            try {
                await logout();
                navigate('/login');
            } catch (err) {
                console.error("Logout error:", err);
            }
        }
    };

    return (
        <>
            <header className="shrink-0 w-full bg-[#0F172A] border-b border-white/5 flex items-center h-16 px-4 md:px-6 lg:px-12 gap-3 md:gap-6 z-50 shadow-xl shadow-black/20 text-white">

                {/* ── BRANDING ── */}
                <div className="flex items-center gap-3 shrink-0">
                    <img
                        src={brandConfig?.logoFull}
                        alt={brandConfig?.logoAlt}
                        className="h-6 md:h-8 w-auto object-contain transition-all hover:scale-110"
                    />

                    {/* ENHANCED PHASE TITLE: Contextual Feedback */}
                    <div className="flex items-center gap-2">
                        <div className="w-px h-5 bg-white/10 mx-1" />
                        <div className="flex flex-col select-none">
                            <span className={`text-[10px] md:text-[11px] font-black uppercase tracking-[0.2em] leading-none italic truncate max-w-[120px] sm:max-w-none ${isReplenishing ? 'text-emerald-400' : 'text-accent'}`}>
                                {isScrolled ? (pageTitle || phaseTitle) : (phaseTitle || (window.location.pathname === '/audit' ? 'Audit Log' : 'Terminal'))}
                            </span>
                            <span className="text-[8px] font-black uppercase tracking-[0.1em] text-white/30 leading-none mt-1 hidden sm:block">
                                {phaseSubtitle || 'Heavy Duty Terminal'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* DIVIDER (Desktop) */}
                <div className="hidden md:block w-px h-8 bg-white/10 shrink-0" />

                {/* ── NAVIGATION: Standardized Mapping (Desktop Only) ── */}
                <div className="hidden md:flex items-center gap-1 flex-1">
                    {activeNavItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            className={({ isActive }) => `
                                ${navItemBaseClass}
                                ${isActive ? activeClass : inactiveClass}
                            `}
                        >
                            {({ isActive }) => (
                                <>
                                    <item.icon size={15} strokeWidth={isActive ? 2.5 : 1.5} />
                                    <span>{item.label}</span>
                                </>
                            )}
                        </NavLink>
                    ))}
                </div>

                {/* ── UTILITIES (Clock + User + Admin Controls) ── */}
                <div className="flex items-center gap-2 md:gap-4 ml-auto">

                    {/* CLOCK DISPLAY */}
                    <div className="hidden sm:flex items-center gap-3 px-4 h-10 bg-white/5 border border-white/10 rounded-xl">
                        <Clock className={isReplenishing ? 'text-emerald-400/50' : 'text-accent/50'} size={14} />
                        <span className="text-[11px] font-mono font-black text-white/90 tracking-widest whitespace-nowrap leading-none">
                            {currentTime?.toLocaleTimeString([], { hour12: false })}
                        </span>
                    </div>

                    {/* USER INFO (Only if NOT in Kiosk Mode) */}
                    {!isTerminalMode && user && (
                        <div className="hidden md:flex items-center gap-2.5 px-3 h-10 bg-white/5 border border-white/10 rounded-xl shrink-0">
                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-black text-[10px] text-white shrink-0 ${user?.avatarColor || 'bg-white/10'}`}>
                                {initials}
                            </div>
                            <div className="flex flex-col leading-none text-left">
                                 <span className="text-[10px] font-black text-white uppercase tracking-tighter truncate max-w-[100px]">{name.split(' ')[0]}</span>
                                <span className="text-[8px] font-bold text-white/30 uppercase tracking-[0.15em] mt-0.5">{user?.role}</span>
                            </div>
                        </div>
                    )}

                    {/* ADMIN CONTROLS - Contextual Unlock Button (Desktop Only) */}
                    {user?.role === 'ADMIN' && (
                        <div className="hidden md:flex items-center gap-2 shrink-0">
                            {isTerminalMode && (
                                <button
                                    onClick={onExit}
                                    className="w-10 h-10 flex items-center justify-center bg-orange-500/10 text-orange-400 border border-orange-500/20 rounded-xl transition-all active:scale-90"
                                    title="Unlock Terminal"
                                >
                                    <Unlock size={18} />
                                </button>
                            )}
                            
                            <button
                                onClick={handleLogout}
                                className="w-10 h-10 flex items-center justify-center bg-white/5 hover:bg-red-500/10 text-white/20 hover:text-red-400 border border-white/10 hover:border-red-500/20 rounded-xl transition-all active:scale-90"
                                title="Exit System"
                            >
                                <LogOut size={16} />
                            </button>
                        </div>
                    )}

                    {/* ── HAMBURGER (Mobile Only - Now on the right) ── */}
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="md:hidden p-2.5 text-white/60 hover:text-white bg-white/5 rounded-xl border border-white/10 transition-all active:scale-95"
                    >
                        {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </header>

            {/* ── MOBILE NAVIGATION DROPDOWN (Direct match with Admin Navbar) ── */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <div className="md:hidden fixed top-16 inset-x-0 z-40 bg-[#0F172A] border-b border-white/10 flex flex-col p-4 gap-2 shadow-2xl animate-in slide-in-from-top-2 duration-200">
                        {activeNavItems.map((item) => {
                            const isActive = window.location.pathname === item.path;
                            return (
                                <button
                                    key={item.path}
                                    onClick={() => handleMobileNav(item.path)}
                                    className={`flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all text-[10px] font-black uppercase tracking-[0.15em] border ${
                                        isActive
                                            ? 'bg-white/10 text-accent border-white/10'
                                            : 'text-white/50 border-transparent hover:text-white hover:bg-white/5'
                                    }`}
                                >
                                    <item.icon size={16} strokeWidth={isActive ? 2.5 : 1.5} />
                                    <span>{item.label}</span>
                                </button>
                            );
                        })}

                        {/* CONTEXTUAL UNLOCK (Mobile Menu Action) */}
                        {isTerminalMode && (
                            <button
                                onClick={() => {
                                    onExit();
                                    setIsMobileMenuOpen(false);
                                }}
                                className="flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all text-[10px] font-black uppercase tracking-[0.15em] bg-orange-500/10 text-orange-400 border border-orange-500/20 mt-1"
                            >
                                <Unlock size={16} strokeWidth={2.5} />
                                <span>Unlock Terminal</span>
                            </button>
                        )}
                        
                        <div className="h-px bg-white/5 my-2" />
                        
                        <div className="flex items-center justify-between px-4 py-3 bg-white/5 rounded-2xl border border-white/5">
                            <div className="flex items-center gap-3">
                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-black text-sm text-white ${user?.avatarColor || 'bg-white/10'}`}>
                                    {initials}
                                </div>
                                <div>
                                    <p className="text-[10px] font-black text-white uppercase tracking-tight leading-none">{name.split(' ')[0]}</p>
                                    <p className="text-[8px] text-white/30 uppercase tracking-widest mt-1">{user?.role}</p>
                                </div>
                            </div>
                            <button 
                                onClick={handleLogout} 
                                className="p-3 bg-white/5 hover:bg-red-500/20 text-white/30 hover:text-red-400 rounded-xl border border-white/5 transition-all active:scale-90"
                            >
                                <LogOut size={15} />
                            </button>
                        </div>
                    </div>
                )}
            </AnimatePresence>

            {/* ── TERMINAL STEPPER (Consolidated Top Bar) ── */}
            {step === 1 && window.location.pathname !== '/audit' && (
                <div className="shrink-0 bg-white border-b border-slate-100 py-3 shadow-sm z-40 animate-in slide-in-from-top-1 duration-300">
                    <div className="max-w-[1850px] mx-auto px-4 md:px-6 lg:px-12">
                        <TerminalStepper step={step} terminalMode={terminalMode} />
                    </div>
                </div>
            )}
        </>
    );
};

TerminalHeader.propTypes = {
    onExit: PropTypes.func.isRequired,
    onActivateKiosk: PropTypes.func.isRequired,
    phaseTitle: PropTypes.string,
    phaseSubtitle: PropTypes.string,
    step: PropTypes.number.isRequired,
    terminalMode: PropTypes.string
};

export default TerminalHeader;
